import { useLocalContent } from "../../../../context";
import Marker from "../Marker";
import * as S from "./styles";
import { School, POI } from "@local-logic/client";
import { baseStyles, Popover } from "@local-logic/core/ui";
import React, { useMemo, useState } from "react";
import Supercluster from "supercluster";
import { UseMeasureRect } from "~/hooks/useMeasure";

const hoverVariants = {
  default: {
    scale: 1.0,
  },
  hovered: {
    scale: 1.2,
  },
};

interface ClusterProps {
  cluster: Supercluster.PointFeature<
    Supercluster.ClusterProperties & Supercluster.AnyProps
  >;
  supercluster:
    | Supercluster<
        {
          poi: POI | School;
          type: string;
        },
        Supercluster.AnyProps
      >
    | undefined;
  pointsCount: number;
  zoom: number;
  rect: UseMeasureRect;
  isMobile: boolean;
  isMapMoving: boolean;
  hoveredPOI: POI | School | undefined;
  activePOI: POI | School | undefined;
  onClick?: (
    cluster: Supercluster.PointFeature<
      Supercluster.ClusterProperties & Supercluster.AnyProps
    >
  ) => void;
}

const clusterListThreshold = 17;

const Cluster = ({
  cluster,
  supercluster,
  pointsCount,
  onClick,
  zoom,
  activePOI,
  hoveredPOI,
  rect,
  isMobile,
  isMapMoving,
}: ClusterProps) => {
  const [showClusterDialog, setShowClusterDialog] = useState(false);
  const { setActivePOI } = useLocalContent();
  const clusterPointCount = cluster.properties.point_count;
  const diameter = 20 + (clusterPointCount / pointsCount) * 25;

  const onClickHandler = () => {
    if (zoom > clusterListThreshold) {
      setShowClusterDialog(true);

      return;
    }

    if (onClick) {
      onClick(cluster);
    }
  };

  const onOpenChangeHandler = (e: boolean) => {
    if (zoom <= clusterListThreshold) {
      return;
    }

    setShowClusterDialog(e);
  };

  const clusterChildren = useMemo(() => {
    if (!supercluster || !cluster.id) {
      return [];
    }

    let children;

    try {
      children = supercluster.getChildren(cluster.id as number);
    } catch (error) {
      // Catch error for "No cluster with the specified id."
      return [];
    }

    if (!children) {
      return [];
    }

    return children.map((c) => c.properties).filter((c) => !!c);
  }, [supercluster, cluster]);

  const clusterChild = clusterChildren?.find(
    (c) => !!activePOI && c.poi === activePOI
  );

  if (clusterChild) {
    return (
      <Marker
        rect={rect}
        poi={clusterChild.poi}
        poiType={clusterChild.type}
        isMobile={isMobile}
        activePOI={activePOI}
        hoveredPOI={hoveredPOI}
        isMapMoving={isMapMoving}
      />
    );
  }

  return (
    <Popover.Root
      open={isMapMoving ? false : showClusterDialog}
      onOpenChange={onOpenChangeHandler}
    >
      <Popover.Trigger>
        <S.Cluster
          diameter={diameter}
          onClick={onClickHandler}
          variants={hoverVariants}
          animate={showClusterDialog ? "hovered" : "default"}
          whileHover={{
            scale: 1.2,
          }}
          transition={baseStyles.transitions.default}
        >
          {cluster.properties.point_count}
        </S.Cluster>
      </Popover.Trigger>
      <Popover.Content side="top">
        <S.ClusterList>
          {clusterChildren?.map((c, i) => (
            <S.ClusterListItem key={i} onClick={() => setActivePOI(c.poi)}>
              {c.poi?.name}
            </S.ClusterListItem>
          ))}
        </S.ClusterList>
      </Popover.Content>
    </Popover.Root>
  );
};

export default Cluster;
